import React, { memo, useMemo } from 'react';
import { Group, Rect, Text as KonvaText } from 'react-konva';
import {
  STOCKER_2D_COLORS,
  STOCKER_2D_DEFAULT_COLUMNS,
  STOCKER_2D_DEFAULT_HEIGHT,
  STOCKER_2D_DEFAULT_ROWS,
  STOCKER_2D_DEFAULT_WIDTH,
  STOCKER_2D_SIMPLE_ZOOM,
  resolveStockerFoupColors,
  resolveStockerStatusColor,
} from './stocker2dPalette';
import {
  STOCKER_2D_DESIGN as D,
  buildOccupiedSlotSet,
  buildStockerSlotKinds,
  getStocker2DLayout,
  getStockerBayLayout,
  stockerColumnKind,
} from './stockerGeometry';
import type { Stocker2DFoupKind, Stocker2DProps, Stocker2DSlot } from './types';

/**
 * Occupied slots keyed `row:column`, each tagged with the FOUP it holds.
 * Explicit `slots` win, then live raw/processed counts, then a plain total.
 */
function resolveOccupied(
  rows: number,
  columns: number,
  slots: Stocker2DSlot[] | undefined,
  occupiedCount: number | undefined,
  rawCount: number | undefined,
  processedCount: number | undefined
): Map<string, Stocker2DFoupKind> {
  if (slots && slots.length > 0) {
    const map = new Map<string, Stocker2DFoupKind>();
    for (const s of slots) {
      if (s.occupied || s.state === 'occupied' || s.state === 'loading') {
        map.set(`${s.row}:${s.column}`, s.kind ?? stockerColumnKind(s.column, columns));
      }
    }
    return map;
  }
  if (typeof rawCount === 'number' || typeof processedCount === 'number') {
    return buildStockerSlotKinds(rows, columns, rawCount ?? 0, processedCount ?? 0);
  }
  if (typeof occupiedCount === 'number') {
    const map = new Map<string, Stocker2DFoupKind>();
    for (const key of buildOccupiedSlotSet(rows, columns, occupiedCount)) {
      const column = Number(key.split(':')[1]);
      map.set(key, stockerColumnKind(column, columns));
    }
    return map;
  }
  // Layout default: empty racks (0 / capacity).
  return new Map();
}

/**
 * Front-facing STK stocker matching the product reference illustration.
 */
export const Stocker2D = memo(function Stocker2D({
  x = 0,
  y = 0,
  width = STOCKER_2D_DEFAULT_WIDTH,
  height = STOCKER_2D_DEFAULT_HEIGHT,
  id = 'STK-01',
  rows = STOCKER_2D_DEFAULT_ROWS,
  columns = STOCKER_2D_DEFAULT_COLUMNS,
  slots,
  occupiedCount,
  rawCount,
  processedCount,
  status = 'available',
  showLabel = true,
  showCapacity = true,
  scale = 1,
  rotation = 0,
  centered = false,
  selected = false,
  hovered = false,
  detail = 'auto',
  zoomLevel = 1,
  opacity = 1,
  listening = false,
  onClick,
  onDblClick,
  onMouseEnter,
  onMouseLeave,
}: Stocker2DProps) {
  const simple =
    detail === 'simple' || (detail === 'auto' && zoomLevel < STOCKER_2D_SIMPLE_ZOOM);
  const statusColor = resolveStockerStatusColor(status);
  const { s, ox, oy } = getStocker2DLayout(width, height);
  const u = (n: number) => n * s;
  const sw = Math.min(u(D.stroke), 1.6 / Math.max(zoomLevel, 0.05));
  const bay = getStockerBayLayout(rows, columns);
  const occupied = useMemo(
    () => resolveOccupied(rows, columns, slots, occupiedCount, rawCount, processedCount),
    [rows, columns, slots, occupiedCount, rawCount, processedCount]
  );
  const totalSlots = rows * columns;
  const occCount = occupied.size;

  const originX = centered ? x - width / 2 : x;
  const originY = centered ? y - height / 2 : y;

  const cabinetH = D.height - D.footH;
  const accentY = cabinetH - D.baseH - D.accentH;
  const baseY = cabinetH - D.baseH;

  const trays: React.ReactNode[] = [];
  if (!simple) {
    for (let r = 0; r < bay.rows; r++) {
      for (let c = 0; c < bay.columns; c++) {
        const key = `${r}:${c}`;
        const tx = bay.startX + c * (bay.slotW + bay.columnGap);
        const ty = bay.startY + r * (bay.slotH + bay.rowGap);
        const kind = occupied.get(key);
        const foup = kind ? resolveStockerFoupColors(kind) : null;
        const inset = 1.3;

        trays.push(
          <Rect
            key={`tray-${key}`}
            x={u(tx)}
            y={u(ty)}
            width={u(bay.slotW)}
            height={u(bay.slotH)}
            fill={STOCKER_2D_COLORS.slotShelf}
            cornerRadius={u(2.2)}
            stroke={STOCKER_2D_COLORS.slotBorder}
            strokeWidth={sw * 0.65}
            listening={false}
            perfectDrawEnabled={false}
            shadowForStrokeEnabled={false}
          />
        );
        // The FOUP fills the whole shelf so occupancy reads at a glance.
        trays.push(
          <Rect
            key={`inner-${key}`}
            x={u(tx + inset)}
            y={u(ty + inset)}
            width={u(bay.slotW - inset * 2)}
            height={u(bay.slotH - inset * 2)}
            fill={foup ? foup.fill : STOCKER_2D_COLORS.slotEmpty}
            cornerRadius={u(1.4)}
            stroke={foup ? foup.stroke : STOCKER_2D_COLORS.slotBorder}
            strokeWidth={sw * 0.45}
            listening={false}
            perfectDrawEnabled={false}
            shadowForStrokeEnabled={false}
          />
        );
      }
    }
  }

  return (
    <Group
      x={originX}
      y={originY}
      scaleX={scale}
      scaleY={scale}
      rotation={rotation}
      opacity={opacity}
      listening={listening}
      onClick={onClick}
      onDblClick={onDblClick}
      onTap={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <Rect width={width} height={height} fill="transparent" listening={listening} perfectDrawEnabled={false} />

      <Group x={ox} y={oy} listening={false}>
        {/* Main light-gray cabinet */}
        <Rect
          width={u(D.width)}
          height={u(cabinetH - D.baseH)}
          fill={STOCKER_2D_COLORS.body}
          cornerRadius={[u(8), u(8), 0, 0]}
          stroke={STOCKER_2D_COLORS.outline}
          strokeWidth={sw}
          perfectDrawEnabled={false}
          shadowForStrokeEnabled={false}
        />

        {/* Neon status pills */}
        <Rect
          name="stocker-status-light"
          x={u(7)}
          y={u(7)}
          width={u(3.5)}
          height={u(13)}
          fill={statusColor}
          cornerRadius={u(1.75)}
          listening={false}
          perfectDrawEnabled={false}
        />
        <Rect
          name="stocker-status-light"
          x={u(D.width - 10.5)}
          y={u(7)}
          width={u(3.5)}
          height={u(13)}
          fill={statusColor}
          cornerRadius={u(1.75)}
          listening={false}
          perfectDrawEnabled={false}
        />

        {showLabel && (
          <KonvaText
            text={id}
            x={u(14)}
            y={u(3)}
            width={u(D.width - 28)}
            height={u(13)}
            align="center"
            verticalAlign="middle"
            fontSize={u(10)}
            fontStyle="bold"
            fontFamily="Arial, Helvetica, sans-serif"
            fill={STOCKER_2D_COLORS.label}
            listening={false}
            perfectDrawEnabled={false}
            wrap="none"
            ellipsis={false}
          />
        )}
        {showLabel && showCapacity && (
          <KonvaText
            text={`${occCount} / ${totalSlots}`}
            x={u(14)}
            y={u(15)}
            width={u(D.width - 28)}
            height={u(10)}
            align="center"
            verticalAlign="middle"
            fontSize={u(7.5)}
            fontFamily="Arial, Helvetica, sans-serif"
            fill={STOCKER_2D_COLORS.capacity}
            listening={false}
            perfectDrawEnabled={false}
            wrap="none"
            ellipsis={false}
          />
        )}

        {/* Recessed storage bay */}
        {!simple && (
          <Rect
            x={u(bay.insetX)}
            y={u(bay.insetY)}
            width={u(bay.insetW)}
            height={u(bay.insetH)}
            fill={STOCKER_2D_COLORS.inset}
            cornerRadius={u(4)}
            stroke={STOCKER_2D_COLORS.outline}
            strokeWidth={sw * 0.7}
            listening={false}
            perfectDrawEnabled={false}
            shadowForStrokeEnabled={false}
          />
        )}

        {trays}

        {simple && (
          <Rect
            x={u(bay.insetX)}
            y={u(bay.insetY)}
            width={u(bay.insetW)}
            height={u(Math.min(bay.insetH, 90))}
            fill={STOCKER_2D_COLORS.inset}
            cornerRadius={u(4)}
            stroke={STOCKER_2D_COLORS.outline}
            strokeWidth={sw * 0.6}
            listening={false}
            perfectDrawEnabled={false}
          />
        )}

        {/* Full-width indigo accent */}
        <Rect
          x={0}
          y={u(accentY)}
          width={u(D.width)}
          height={u(D.accentH)}
          fill={STOCKER_2D_COLORS.accentBlue}
          listening={false}
          perfectDrawEnabled={false}
        />

        {/* Dark base — slightly wider than cabinet */}
        <Rect
          x={u(-D.baseOverhang)}
          y={u(baseY)}
          width={u(D.width + D.baseOverhang * 2)}
          height={u(D.baseH)}
          fill={STOCKER_2D_COLORS.outline}
          listening={false}
          perfectDrawEnabled={false}
        />

        {/* Feet */}
        <Rect
          x={u(8)}
          y={u(cabinetH)}
          width={u(D.footW)}
          height={u(D.footH)}
          fill={STOCKER_2D_COLORS.outline}
          cornerRadius={u(1)}
          listening={false}
          perfectDrawEnabled={false}
        />
        <Rect
          x={u(D.width - 8 - D.footW)}
          y={u(cabinetH)}
          width={u(D.footW)}
          height={u(D.footH)}
          fill={STOCKER_2D_COLORS.outline}
          cornerRadius={u(1)}
          listening={false}
          perfectDrawEnabled={false}
        />

        {(selected || hovered) && (
          <Rect
            x={u(-D.baseOverhang - 2)}
            y={u(-2)}
            width={u(D.width + D.baseOverhang * 2 + 4)}
            height={u(cabinetH + D.footH + 2)}
            fillEnabled={false}
            stroke={selected ? STOCKER_2D_COLORS.selection : STOCKER_2D_COLORS.hover}
            strokeWidth={sw}
            cornerRadius={u(8)}
            listening={false}
            perfectDrawEnabled={false}
            shadowForStrokeEnabled={false}
          />
        )}
      </Group>
    </Group>
  );
});
